import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

import { getTicketTypesByEventId, createTicketType, updateTicketType, deleteTicketType } from '../../../services/ticketService';

const TicketingTab = ({ event, onSave }) => {
  const [ticketTypes, setTicketTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (event?.id) {
      loadTicketTypes();
    }
  }, [event?.id]);

  const loadTicketTypes = async () => {
    try {
      setLoading(true);
      const data = await getTicketTypesByEventId(event?.id);
      setTicketTypes(data || []);
    } catch (error) {
      setError(`Failed to load ticket types: ${error?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = () => {
    setEditingTicket(null);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleEditTicket = (ticket) => {
    setEditingTicket(ticket);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to delete this ticket type?')) {
      return;
    }

    try {
      setError('');
      await deleteTicketType(ticketId);
      setSuccess('Ticket type deleted successfully');
      loadTicketTypes();
    } catch (error) {
      setError(`Failed to delete ticket type: ${error?.message}`);
    }
  };

  const handleSaveTicket = async (ticketData) => {
    try {
      setError('');
      setLoading(true);

      if (editingTicket?.id) {
        await updateTicketType(editingTicket?.id, ticketData);
        setSuccess('Ticket type updated successfully');
      } else {
        await createTicketType({ ...ticketData, event_id: event?.id });
        setSuccess('Ticket type created successfully');
      }

      setShowForm(false);
      setEditingTicket(null);
      loadTicketTypes();
    } catch (error) {
      setError(`Failed to save ticket type: ${error?.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!event?.id) {
    return (
      <div className="p-6 text-center">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl text-muted-foreground">🎫</span>
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No Event Selected</h3>
        <p className="text-sm text-muted-foreground">
          Please create or select an event first to manage ticket types
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Ticket Types</h2>
          <p className="text-sm text-muted-foreground">
            Manage ticket tiers, pricing, and availability for {event?.title}
          </p>
        </div>
        <Button
          variant="default"
          onClick={handleCreateTicket}
          iconName="Plus"
        >
          Add Ticket Type
        </Button>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}
      {/* Ticket Types List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {ticketTypes?.map(ticket => (
          <div key={ticket?.id} className="bg-card border border-border rounded-lg p-4">
            <div className="flex justify-between items-start mb-3">
              <h3 className="font-medium text-foreground">{ticket?.name}</h3>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEditTicket(ticket)}
                  iconName="Edit"
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteTicket(ticket?.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  Delete
                </Button>
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Price:</span>
                <span className="font-medium">₹{ticket?.price}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Available:</span>
                <span>{ticket?.quantity_available}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sold:</span>
                <span>{ticket?.quantity_sold || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Max per order:</span>
                <span>{ticket?.max_per_order}</span>
              </div>
            </div>

            {ticket?.description && (
              <p className="text-sm text-muted-foreground mt-3 line-clamp-2">
                {ticket?.description}
              </p>
            )}

            <div className={`mt-3 px-2 py-1 rounded-full text-xs inline-block ${
              ticket?.is_active 
                ? 'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
            }`}>
              {ticket?.is_active ? 'Active' : 'Inactive'}
            </div>
          </div>
        ))}
      </div>
      {/* Ticket Form Modal/Panel */}
      {showForm && (
        <TicketForm
          ticket={editingTicket}
          onSave={handleSaveTicket}
          onCancel={() => {
            setShowForm(false);
            setEditingTicket(null);
          }}
          loading={loading}
        />
      )}
    </div>
  );
};

const TicketForm = ({ ticket, onSave, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    quantity_available: '',
    max_per_order: '10',
    sales_start: '',
    sales_end: '',
    benefits: [],
    is_active: true
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (ticket) {
      setFormData({
        name: ticket?.name || '',
        description: ticket?.description || '',
        price: ticket?.price?.toString() || '',
        quantity_available: ticket?.quantity_available?.toString() || '',
        max_per_order: ticket?.max_per_order?.toString() || '10',
        sales_start: ticket?.sales_start ? new Date(ticket.sales_start)?.toISOString()?.slice(0, 16) : '',
        sales_end: ticket?.sales_end ? new Date(ticket.sales_end)?.toISOString()?.slice(0, 16) : '',
        benefits: ticket?.benefits || [],
        is_active: ticket?.is_active ?? true
      });
    }
  }, [ticket]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Ticket name is required';
    }

    if (!formData?.price || isNaN(parseFloat(formData?.price))) {
      newErrors.price = 'Valid price is required';
    }

    if (!formData?.quantity_available || isNaN(parseInt(formData?.quantity_available))) {
      newErrors.quantity_available = 'Valid quantity is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      price: parseFloat(formData?.price),
      quantity_available: parseInt(formData?.quantity_available),
      max_per_order: parseInt(formData?.max_per_order),
      sales_start: formData?.sales_start ? new Date(formData.sales_start)?.toISOString() : null,
      sales_end: formData?.sales_end ? new Date(formData.sales_end)?.toISOString() : null
    };

    await onSave?.(submitData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-foreground">
              {ticket ? 'Edit Ticket Type' : 'Create Ticket Type'}
            </h3>
            <Button variant="outline" onClick={onCancel}>
              ×
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <Input
                label="Ticket Name"
                placeholder="e.g., General Admission"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                error={errors?.name}
                required
              />

              <Input
                label="Description"
                placeholder="Describe this ticket type"
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                multiline
                rows={3}
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Price (₹)"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData?.price}
                  onChange={(e) => handleInputChange('price', e?.target?.value)}
                  error={errors?.price}
                  required
                />

                <Input
                  label="Quantity Available"
                  type="number"
                  placeholder="100"
                  value={formData?.quantity_available}
                  onChange={(e) => handleInputChange('quantity_available', e?.target?.value)}
                  error={errors?.quantity_available}
                  required
                />
              </div>
            </div>

            <div className="space-y-4">
              <Input
                label="Max Per Order"
                type="number"
                placeholder="10"
                value={formData?.max_per_order}
                onChange={(e) => handleInputChange('max_per_order', e?.target?.value)}
              />

              <Input
                label="Sales Start"
                type="datetime-local"
                value={formData?.sales_start}
                onChange={(e) => handleInputChange('sales_start', e?.target?.value)}
              />

              <Input
                label="Sales End"
                type="datetime-local"
                value={formData?.sales_end}
                onChange={(e) => handleInputChange('sales_end', e?.target?.value)}
              />

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData?.is_active}
                  onChange={(e) => handleInputChange('is_active', e?.target?.checked)}
                  className="rounded border-border"
                />
                <span className="text-sm text-foreground">Active (available for sale)</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-border">
            <Button variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button variant="default" onClick={handleSubmit} loading={loading}>
              {ticket ? 'Update Ticket' : 'Create Ticket'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketingTab;